import dayjs from "dayjs";
import { setFont } from "@similarweb/styles";
import { colorsPalettes, rgba, fonts, colorsSets } from "@similarweb/styles";
import type { FC } from "react";
import type React from "react";
import { TrendsBar } from "pro-features/components/TrendsBar/src/TrendsBar";

import styled from "styled-components";
import { abbrNumberVisitsFilter } from "../../filters/ngFilters";
import { dynamicFilterFilter } from "app/filters/dynamicFilter";
import { getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";

interface IKeywordAnalysisTrendsBarProps {
    data?: Array<{ [key: string]: number }>;
    barsLimit?: number;
    inverted?: boolean;
    tooltipComponent?: FC<{ date: string; value: any; prevValue?: any }>;
}

interface IKeywordAnalysisTrendsBarContainerProps extends IKeywordAnalysisTrendsBarProps {
    dataVolume: number;
    trendsBar?: any;
    showUpperLine?: boolean;
    volumeThreshold?: number;
}

const dateFormat = (date) => dayjs(date).format("MMM, YYYY");
const Container = styled.div`
    height: 48px;
`;

const RightCorner = styled.div`
    position: absolute;
    right: 20px;
    bottom: 15px;
`;
const Value = styled.label`
    @media (min-width: 1381px) {
        ${setFont({
            $family: fonts.$dmSansFontFamily,
            $weight: 300,
            $size: 38,
            $color: rgba(colorsPalettes.midnight[500], 0.8),
        })}
    }
    ${setFont({
        $family: fonts.$dmSansFontFamily,
        $weight: 300,
        $size: 32,
        $color: rgba(colorsPalettes.midnight[500], 0.8),
    })}
    cursor: default;
`;

const KeywordAnalysisTrendsBarDefaultTooltip = ({ date, value }) => {
    return (
        <span>
            <strong>{`${abbrNumberVisitsFilter()(value)}`}</strong>
            {` searches in ${dateFormat(date)}`}
        </span>
    );
};

const KeywordAnalysisTrendsBar: FC<IKeywordAnalysisTrendsBarProps> = ({
    data,
    barsLimit,
    inverted,
    tooltipComponent: TooltipComponent = KeywordAnalysisTrendsBarDefaultTooltip,
}) => {
    const dataWithTooltips = data.map((item, index) => {
        const [key, value] = Object.entries(item)[0];
        const prevItem = index > 0 ? Object.entries(data[index - 1])[0] : undefined;
        return {
            value,
            tooltip: (
                <TooltipComponent
                    date={key}
                    value={value}
                    prevValue={prevItem?.[1]}
                />
            ),
        };
    });
    return (
        <Container>
            <TrendsBar
                values={dataWithTooltips}
                barsLimit={barsLimit}
                inverted={inverted}
                colorsSet={[colorsPalettes.blue[500], colorsSets.c["others"]]}
            />
        </Container>
    );
};


const FlexBox = styled.div`
    margin-top: 5px;
    display: flex;
    justify-content: space-between;
`;

const TrendsBarContainer = styled.div`
    margin-top: 5px;
    display: flex;
    position: relative;
    left: 0px;
    bottom: 20px;
    height: 30px;
`;

const Hr = styled.hr`
    margin-top: 15px;
    margin-bottom: 24px;
`;
export const KeywordAnalysisTrendsBarContainer: React.FunctionComponent<IKeywordAnalysisTrendsBarContainerProps> = ({
    showUpperLine = true,
    volumeThreshold = 5000,
    ...props
}) => {
    return (
        <>
            {showUpperLine ? <Hr /> : null}
            <FlexBox>
                <Value>
                    {dynamicFilterFilter()(
                        getVolumeValue(props.dataVolume),
                        `minVisitsAbbr${volumeThreshold ? `:${volumeThreshold}` : ""}`,
                    )}
                </Value>
                {props.data && props.dataVolume > volumeThreshold ? (
                    <TrendsBarContainer>
                        <KeywordAnalysisTrendsBar {...props} />
                    </TrendsBarContainer>
                ) : null}
                {props.trendsBar ? (
                    <TrendsBarContainer>
                        <div style={{ height: 48 }}>{props.trendsBar}</div>
                    </TrendsBarContainer>
                ) : null}
            </FlexBox>
        </>
    );
};
